<script lang="ts" setup>
import { PropType, computed, useSlots } from 'vue'

const slots = useSlots()

const skeletonPresets = [
  'table',
  'article',
  'list-item@5',
  'image, text',
] as const
type SkeletonPreset = (typeof skeletonPresets)[number]

const {
  mobileFab,
  isMobile,
  onlyWhite,
  color,
  loading,
  skeletonType,
  bgWhite,
  subheaderFullBleed,
  header,
  constrainWidth,
  maxWidth,
  contentTopPadding,
} = defineProps({
  mobileFab: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
  onlyWhite: { type: Boolean, default: false },
  bgWhite: { type: Boolean, default: false },
  color: { type: String, default: null, required: false },
  loading: { type: Boolean, default: false },
  skeletonType: {
    type: String as PropType<SkeletonPreset | string>,
    default: 'table',
  },
  subheaderFullBleed: { type: Boolean, default: false },
  header: { type: Boolean, default: true },
  constrainWidth: { type: Boolean, default: true },
  maxWidth: {
    type: [Number, String] as PropType<number | string>,
    default: 1200,
  },
  contentTopPadding: {
    type: String as PropType<'auto' | 'none' | 'subheader' | 'default'>,
    default: 'auto',
  },
})

const contentBgClass = computed(() =>
  bgWhite || onlyWhite || isMobile ? 'bg-white' : 'bg-grey-lighten-5'
)

const headerColor = computed(() => {
  if (color) return 'transparent'
  if (bgWhite) return 'white'
  return !isMobile ? 'grey-lighten-5' : 'white'
})

const topPadStyle = computed((): Record<string, string> => {
  if (isMobile) return { paddingTop: '0px' }

  if (contentTopPadding === 'none') return { paddingTop: '0px' }
  if (contentTopPadding === 'subheader') return { paddingTop: '64px' }
  if (contentTopPadding === 'default') return { paddingTop: '24px' }

  if (slots.subheader) return { paddingTop: '64px' }
  return { paddingTop: '48px' }
})

const normalizeMaxWidth = (value: number | string): string => {
  if (typeof value === 'number') return `${value}px`
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return `${trimmed}px`
  return trimmed
}

const resolvedMaxWidth = computed(() =>
  constrainWidth ? normalizeMaxWidth(maxWidth) : 'none'
)

const contentWrapStyle = computed<Record<string, string>>(() => ({
  maxWidth: resolvedMaxWidth.value,
}))

const contentWrapClass = computed(() => (constrainWidth ? 'mx-md-auto' : ''))

const subheaderInnerStyle = computed<Record<string, string>>(() => ({
  maxWidth: subheaderFullBleed ? 'none' : resolvedMaxWidth.value,
}))

const showMobileTitlebar = computed(() => isMobile && header)
</script>

<template>
  <v-app>
    <v-main
      :style="{
        marginTop: '64px',
      }"
    >
      <v-sheet
        v-if="header"
        class="d-flex align-center position-relative"
        :color="headerColor"
        :height="48"
      >
        <div v-if="color" class="header-color-abs">
          <slot name="header-color" />
        </div>
      </v-sheet>

      <div
        v-if="$slots.subheader"
        class="eve-subheader-wrap"
        :class="[
          { 'eve-subheader-full': subheaderFullBleed },
          { 'eve-subheader-mobile': isMobile },
          { 'eve-subheader-after-mobile-titlebar': showMobileTitlebar },
        ]"
      >
        <div
          class="eve-subheader-inner"
          :class="isMobile ? 'px-4' : 'px-md-4'"
          :style="subheaderInnerStyle"
        >
          <slot name="subheader" />
        </div>
      </div>
      <!-- CONTEÚDO -->
      <v-container
        fluid
        class="d-flex flex-column flex-grow-1 px-0 pb-0"
        :class="[contentBgClass]"
        :style="topPadStyle"
      >
        <div
          class="w-100 flex-grow"
          :class="[contentWrapClass, isMobile ? 'pa-0' : 'px-md-4']"
          :style="contentWrapStyle"
        >
          <transition name="fade-fast" mode="out-in">
            <div
              :key="loading ? 'skeleton' : 'content'"
              :role="loading ? 'status' : undefined"
              :aria-busy="loading ? 'true' : 'false'"
              aria-live="polite"
            >
              <template v-if="loading">
                <slot name="skeleton" :type="skeletonType">
                  <v-skeleton-loader
                    :type="skeletonType"
                    class="rounded-lg"
                    boilerplate
                  />
                </slot>
              </template>

              <template v-else>
                <slot />
              </template>
            </div>
          </transition>
        </div>

        <!-- FAB mobile -->
        <v-btn
          v-if="mobileFab && isMobile"
          icon="mdi-plus"
          color="primary"
          class="position-fixed z-2 elevation-2 bottom-14 right-10"
          aria-label="Criar"
        />
      </v-container>
    </v-main>
  </v-app>
</template>

<style scoped>
:deep(.v-sheet) {
  overflow: visible;
}

.header-color-abs {
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

/* =========================
   TRANSITION
   ========================= */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.12s ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

/* =========================
   SUBHEADER (desktop default)
   ========================= */
.eve-subheader-wrap {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
}

.eve-subheader-inner {
  width: 100%;
  margin: 0;
  justify-content: flex-start;
}

.eve-subheader-full .eve-subheader-inner {
  max-width: none;
}

/* =========================
   SUBHEADER (mobile fix)
   ========================= */
.eve-subheader-mobile {
  height: auto;
  flex-direction: column;
}

.eve-subheader-mobile .eve-subheader-inner {
  display: flex;
  flex-direction: column;
}

.eve-subheader-after-mobile-titlebar {
  margin-top: 0 !important;
}

.eve-breadcrumbs .v-breadcrumbs-item--disabled {
  opacity: 1 !important;
}

.eve-breadcrumbs .green-breadcrumbs {
  opacity: 1 !important;
  color: var(--standart-theme---EVE-primary, #8dc63f) !important;
}

@media (max-width: 600px) {
  .eve-subheader-wrap {
    border-radius: 0 !important;
  }

  :deep(.v-main) {
    padding: 0 !important;
  }

  :deep(.v-application),
  :deep(.v-application__wrap),
  :deep(.v-main) {
    background: #fff !important;
  }
}
</style>
